export type ConditionOption = { value: string; label: string };

export type AttachmentRuleField = {
  section: string;
  key: string;
  label: string;
  values: ConditionOption[];
};

const condition: ConditionOption[] = [
  { value: 'good', label: 'Good' },
  { value: 'needs_attention', label: 'Needs Attention' },
  { value: 'requires_service', label: 'Requires Service' },
];

const conditionWithNa: ConditionOption[] = [
  ...condition,
  { value: 'n_a', label: 'N/A' },
];

const yesNo: ConditionOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const hvacAge: ConditionOption[] = [
  { value: '1_10', label: '1–10 years' },
  { value: '11_15', label: '11–15 years' },
  { value: '15_plus', label: '15+ years' },
];

const plumbingAge: ConditionOption[] = [
  { value: 'n_a', label: 'N/A' },
  { value: '1_6', label: '1–6 years' },
  { value: '6_9', label: '6–9 years' },
  { value: '10_15', label: '10–15 years' },
];

const yesNoNa: ConditionOption[] = [
  ...yesNo,
  { value: 'n_a', label: 'N/A' },
];

export const ATTACHMENT_RULE_FIELDS: AttachmentRuleField[] = [
  // HVAC
  { section: 'HVAC', key: 'hvac_age', label: 'Age', values: hvacAge },
  { section: 'HVAC', key: 'hvac_overall_visual_condition', label: 'Overall Visual Condition', values: condition },
  { section: 'HVAC', key: 'hvac_overall_operation', label: 'Overall Operation', values: condition },
  { section: 'HVAC', key: 'hvac_proper_clearance', label: 'Proper Clearance', values: yesNo },
  { section: 'HVAC', key: 'hvac_service_record_updated', label: 'Service Record Updated', values: yesNo },
  { section: 'HVAC', key: 'hvac_boiler_ac_present', label: 'Air Conditioning', values: yesNo },
  { section: 'HVAC', key: 'hvac_boiler_expansion_tank_condition', label: 'Expansion Tank', values: conditionWithNa },
  { section: 'HVAC', key: 'hvac_boiler_heat_exchanger_condition', label: 'Heat Exchanger', values: conditionWithNa },
  { section: 'HVAC', key: 'hvac_boiler_pressure_relief_condition', label: 'Pressure Relief', values: conditionWithNa },
  { section: 'HVAC', key: 'hvac_boiler_auto_fill_condition', label: 'Auto-Fill', values: conditionWithNa },
  // Plumbing
  { section: 'Plumbing', key: 'plumbing_age', label: 'Age', values: plumbingAge },
  { section: 'Plumbing', key: 'plumbing_overall_visual_condition', label: 'Overall Visual Condition', values: condition },
  { section: 'Plumbing', key: 'plumbing_expansion_tank_condition', label: 'Expansion Tank Condition', values: conditionWithNa },
  { section: 'Plumbing', key: 'plumbing_visible_leaks_or_rust', label: 'Visible Leaks or Rust', values: yesNo },
  { section: 'Plumbing', key: 'plumbing_proper_clearance', label: 'Proper Clearance', values: yesNo },
  { section: 'Plumbing', key: 'plumbing_recommended_annual_maintenance', label: 'Recommended Annual Maintenance', values: yesNo },
  { section: 'Plumbing', key: 'plumbing_installed_coupon', label: 'Installed Coupon', values: yesNo },
  // Electrical
  { section: 'Electrical', key: 'electrical_overall_visual_condition', label: 'Overall Visual Condition', values: condition },
  { section: 'Electrical', key: 'electrical_whole_house_surge_protection', label: 'Whole House Surge Protection', values: conditionWithNa },
  { section: 'Electrical', key: 'electrical_wiring_secured_properly_terminated', label: 'Wiring Secured / Properly Terminated', values: condition },
  { section: 'Electrical', key: 'electrical_whole_home_generator_condition', label: 'Whole Home Generator Condition', values: conditionWithNa },
  { section: 'Electrical', key: 'electrical_proper_clearance', label: 'Proper Clearance', values: yesNo },
  // Water Treatment
  { section: 'Water Treatment', key: 'water_treatment_visible_condition', label: 'Visible Condition', values: conditionWithNa },
  { section: 'Water Treatment', key: 'water_treatment_recommended_annual_maintenance', label: 'Recommended Annual Maintenance', values: yesNo },
  { section: 'Water Treatment', key: 'water_treatment_filters_or_salt_required', label: 'Filters or Salt Required', values: yesNo },
  { section: 'Water Treatment', key: 'water_treatment_water_quality_complaints', label: 'Water Quality Complaints', values: yesNo },
  { section: 'Water Treatment', key: 'water_treatment_location_for_solution', label: 'Location for Solution', values: yesNo },
  // Air Quality
  { section: 'Air Quality', key: 'air_quality_air_filter_condition', label: 'Air Filter Condition', values: condition },
  { section: 'Air Quality', key: 'air_quality_humidifier_status', label: 'Humidifier Status', values: conditionWithNa },
  { section: 'Air Quality', key: 'air_quality_dehumidifier_status', label: 'Dehumidifier Status', values: conditionWithNa },
  // General
  { section: 'General', key: 'offered_maintenance_plan', label: 'Offered Maintenance Plan', values: yesNoNa },
];

export const SECTIONS = [...new Set(ATTACHMENT_RULE_FIELDS.map((f) => f.section))];

export function getFieldByKey(key: string): AttachmentRuleField | undefined {
  return ATTACHMENT_RULE_FIELDS.find((f) => f.key === key);
}

export function getValueLabel(fieldKey: string, value: string): string {
  const field = getFieldByKey(fieldKey);
  return field?.values.find((v) => v.value === value)?.label ?? value;
}
